import React from 'react';
import PropTypes from 'prop-types';
import {formatMoney} from '@shopify/theme-currency';

import iconFastForwrd from '../../../../assets/svg/icon-fast-forward.svg';

const FreeShippingBar = ({total, freeShippingBarSettings, isCartPage}) => {
  const {remainingTemplate, reachedTemplate, serviceOne, serviceTwo, thresholdOne, thresholdTwo} =
    freeShippingBarSettings;

  /**
   * Calculate the progress percentage for a service bar.
   * @param {number} cartTotal - Current cart total
   * @param {number} threshold - Threshold amount for the service
   * @returns {number} - Progress percentage (0-100)
   */
  const calculateProgressPercentage = (cartTotal, threshold) => {
    if (cartTotal <= 0) return 0;
    if (cartTotal >= threshold) return 100;
    return Math.min((cartTotal / threshold) * 100, 100);
  };

  /**
   * Get the progress percentage for service one.
   * @returns {number}
   */
  const getServiceOneProgress = () => calculateProgressPercentage(total, thresholdOne);

  /**
   * Get the progress percentage for service two.
   * @returns {number}
   */
  const getServiceTwoProgress = () => {
    if (!thresholdTwo) return 0;

    // If service one threshold is met, calculate progress for service two
    if (total >= thresholdOne) {
      // Calculate progress based on amount spent beyond the first threshold
      const amountBeyondFirstThreshold = total - thresholdOne;
      const remainingToSecondThreshold = thresholdTwo - thresholdOne;
      return calculateProgressPercentage(amountBeyondFirstThreshold, remainingToSecondThreshold);
    }

    // If service one threshold is not met, service two should be 0%
    return 0;
  };

  /**
   * Create the free shipping remaining spend message.
   * @param {number} cartTotal
   * @param {number} threshold
   * @param {string} service
   * @returns {string}
   */
  const createRemainingMessage = (cartTotal, threshold, service) => {
    const remainingAmt = formatMoney(threshold - cartTotal, window.theme.moneyFormat);
    return remainingTemplate.replace('{{ amount }}', remainingAmt).replace('{{ service }}', service);
  };

  /**
   * Construct free shipping bar percentage and message.
   *
   * @return {{message:String}}
   */
  const calcFreeShippingStatus = () => {
    if (total < thresholdOne) {
      return {
        barMessage: createRemainingMessage(total, thresholdOne, serviceOne),
      };
    } else if (thresholdTwo && total < thresholdTwo) {
      return {
        barMessage: createRemainingMessage(total, thresholdTwo, serviceTwo),
      };
    } else {
      const service = thresholdTwo ? serviceTwo : serviceOne;
      return {
        barMessage: reachedTemplate.replace('{{ service }}', service),
      };
    }
  };

  const {barMessage} = calcFreeShippingStatus();
  const serviceOneProgress = getServiceOneProgress();
  const serviceTwoProgress = getServiceTwoProgress();

  // Don't render the free shipping bar if cart is empty
  if (total <= 0) {
    return null;
  }

  return (
    <div className={isCartPage ? 'cart__free-shipping container container--md' : 'cart__free-shipping'}>
      <div className="free-shipping-bar__services">
        <div className="free-shipping-bar__service service-one">
          <p>{serviceOne}</p>
          <div className="free-shipping-bar__service-progress">
            <div className="free-shipping-bar__service-progress-bar" style={{width: `${serviceOneProgress}%`}} />
          </div>
        </div>
        {serviceTwo && (
          <>
            <span className="free-shipping-bar__service-icon" dangerouslySetInnerHTML={{__html: iconFastForwrd}} />
            <div className="free-shipping-bar__service service-two">
              <p>{serviceTwo}</p>
              <div className="free-shipping-bar__service-progress">
                <div className="free-shipping-bar__service-progress-bar" style={{width: `${serviceTwoProgress}%`}} />
              </div>
            </div>
          </>
        )}
      </div>
      <p>{barMessage}</p>
    </div>
  );
};

FreeShippingBar.propTypes = {
  total: PropTypes.number,
  freeShippingBarSettings: PropTypes.shape({
    enabled: PropTypes.bool.isRequired,
    remainingTemplate: PropTypes.string.isRequired,
    reachedTemplate: PropTypes.string.isRequired,
    serviceOne: PropTypes.string.isRequired,
    serviceTwo: PropTypes.string,
    thresholdOne: PropTypes.number.isRequired,
    thresholdTwo: PropTypes.number,
  }),
  isCartPage: PropTypes.bool,
};

export default FreeShippingBar;
